import {
  Button,
  CircularProgress,
  makeStyles,
  withStyles,
  Grid,
  Typography
} from '@material-ui/core'
import { TFunction } from 'i18next'
import { Fragment, useState, useEffect } from 'react'
import clsx from 'clsx'
import {
  Navigate,
  OpenDialog,
  CloseDialog,
  UpdateStatusWithForm
} from '../../../actions/types'
import customCloseIcon from '../../../images/customCloseIcon.svg'
import { MuiClasses } from '../../../types'
import { Application, ApplicationStatus } from '../../../types/applications'
import { Apartment } from '../../../types/apartments'
import Suggest from '../../Apartment/Stepper/Steps/Suggest'
import Dialog from '../../Dialog'
import ApplicationsApi from '../../../services/applications'

const styles = () => ({
  primaryBtn: {
    backgroundColor: '#4f9b44',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingBottom: '16px',
    marginRight: '16px',
  },
  actionButton: {
    padding: '0.3rem 1.4rem',
    marginBottom: '16px',
    marginLeft: '16px',
    fontSize: '12px'
  },
  greenActionButton: {
    backgroundColor: '#4f9b44',
    color: '#FFFFFF',
    borderColor: '#4f9b44',
    border: '1px solid #4f9b44',
    '&:hover': {
      backgroundColor: '#3f7c36',
      borderColor: '#3f7c36',
      border: '1px solid #3f7c36'
    }
  },
  selectedActionButton: {
    backgroundColor: '#ffffff',
    color: '#4f9b44',
    borderColor: '#4f9b44',
    border: '1px solid #4f9b44',
    '&:hover': {
      backgroundColor: '#ffffff',
      borderColor: '#4f9b44',
      border: '1px solid #4f9b44'
    }
  },
  chatUsed: {
    color: '#fff',
    background: '#4f9b44',
    padding: '0.1rem 0.6rem',
    lineHeight: '20px',
    alignSelf: 'flex-end',
    marginTop: '0px',
    marginBottom: '16px',
    fontSize: '0.8rem',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-14px',
      width: 0,
      height: 0,
      borderTop: '20px solid #4f9b44',
      borderLeft: '15px solid transparent'
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '-14px',
      width: 0,
      height: 0,
      borderBottom: '20px solid #4f9b44',
      borderLeft: '15px solid transparent'
    }
  }
})

const useStyles = makeStyles({
  previewPhone: {
    boxSizing: 'border-box',
    paddingBottom: '16px',
    color: '#A2A4AF',
    fontSize: '20px ',
    paddingLeft: '8px',
    '& div': {
      paddingTop: 0,
      paddingBottom: 0
    }
  }
})

interface ApplicationCardButtonsProps {
  t: TFunction
  classes: MuiClasses
  isSelected?: boolean
  application: Application
  selectApplication: (applicationId: string) => void
  chatWithApplicant: (applicationId: string) => void
  openDialog: OpenDialog
  closeDialog: CloseDialog
  // invitableAndRejectableStatus: boolean
  // onlyInviteAllowed: boolean
  disableActions: boolean
  invitable: boolean
  PhoneDisplay: any
  status: string
  updateStatusWithForm: UpdateStatusWithForm
  navigate: Navigate
  isAdmin?: boolean
  apartment: Apartment
  moveToContractPage?: () => void
  cancelInvitations?: () => void
}

const ConfirmModel = ({
  classes,
  t,
  dialogId,
  closeDialogAction,
  confirmDialogAction,
  loading
}: {
  classes: MuiClasses
  t: TFunction
  dialogId: string
  loading: boolean
  closeDialogAction: CloseDialog
  confirmDialogAction: () => void
}) => {
  return (
    <Grid
      container
      justify='center'
      style={{ maxWidth: '800px', margin: 'auto' }}
    >
      <Typography variant='h5' align='center' className={classes.modalTitle}>
        {t('confirmTenant.label')}
      </Typography>
      <Grid item sm={12} style={{ margin: '15px 0 20px 0' }}>
        <Suggest
          isModal
          containerMaxWidth='100%'
          contentMaxWidth='100%'
          content={t('confirmTenant.confirmMessage')}
        />
      </Grid>

      <Grid container spacing={2}>
        <Grid item sm={12} xs={12}>
          <Button
            variant='contained'
            fullWidth
            className={classes.primaryBtn}
            onClick={() => {
              confirmDialogAction()
              // closeDialogAction(dialogId)
            }}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={21}
                  style={{ color: '#4f9b44', marginLeft: '5px' }}
                />
              </>
            ) : (
              <>{t('confirmTenant.confirmButtonText')}</>
            )}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}

const ApplicationCardButtons = ({
  t,
  classes,
  isSelected,
  application: { applicationId, apartmentId },
  application,
  openDialog,
  closeDialog,
  selectApplication,
  chatWithApplicant,
  invitable,
  disableActions,
  PhoneDisplay,
  status,
  updateStatusWithForm,
  navigate,
  isAdmin,
  moveToContractPage,
  cancelInvitations,
  apartment
}: // invitableAndRejectableStatus,
  // onlyInviteAllowed,
  //disableAction
  ApplicationCardButtonsProps) => {
  const myClasses = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [chatUsed, setChatUsed] = useState(false)

  useEffect(() => {
    if (!isAdmin) return
    console.log("running checkChatUsage")
    const checkChatUsage = async () => {
      try {
        const response = await ApplicationsApi.checkIfLandlordUsedChat(
          apartmentId,
          applicationId,
          apartment.userId
        )
        setChatUsed(response.landlordUsedChat)
      } catch (error) {
        console.error('Error checking if chat is used', error)
      }
    }

    checkChatUsage()
  }, [applicationId, apartmentId, apartment])

  const canGenerateContract =
    Boolean(application.applicants) &&
    typeof moveToContractPage === 'function'

  return (
    <>
      <section className={classes.actionButtons}>
        {!disableActions && status === ApplicationStatus.invitationsSent && application.applicants && (
          <span>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => openDialog(`cancelInvitationsDialog-${applicationId}`)}
              className={classes.actionButton}
              style={!isSelected ? { color: '#f00' } : { color: '#707070' }}
            >
              {t('applicantCard.cancelInvitationsShort')}
            </Button>
          </span>
        )}
        {/* {(!disableActions && (invitable || (!application.applicants && ApplicationStatus.wantsContract))) && ( */}
        {!disableActions && invitable && (
          <Fragment>
            <span>
              <Button
                variant='contained'
                color='inherit'
                onClick={() => selectApplication(applicationId)}
                className={clsx(classes.actionButton, {
                  [classes.greenActionButton]: !isSelected,
                  [classes.selectedActionButton]: isSelected
                })}
              >
                {isSelected
                  ? t('general.selected')
                  : status === ApplicationStatus.accepted
                    ? t('general.selectInvited')
                    : t('general.select')}
              </Button>
            </span>
          </Fragment>
        )}
        {!isAdmin && window.talkSession && (
          <span>
            <Button
              variant='contained'
              color='primary'
              onClick={() => chatWithApplicant(applicationId)}
              className={clsx(classes.actionButton, classes.greenActionButton)}
            >
              {t('general.chat')}
            </Button>
          </span>
        )}
        {!disableActions && (
          <Fragment>
            <span>
              <Button
                variant='contained'
                color='primary'
                disabled={!canGenerateContract}
                className={clsx(classes.actionButton, {
                  [classes.greenActionButton]: canGenerateContract
                })}
                onClick={() => canGenerateContract && moveToContractPage?.()}
              >
                {t('applicantCard.generateContract')}
              </Button>
            </span>
          </Fragment>
        )}
        {/* {!disableActions && status != ApplicationStatus.wantsContract && (
          <span>
            <Button
              variant='outlined'
              color='primary'
              onClick={() => sendContractInfoRequest(application, apartment)}
              className={classes.actionButton}
            >
              {t('applicantCard.requestContractInfo')}
            </Button>
          </span>
        )} */}
      </section>
      {/*******  https://dwellet.atlassian.net/browse/FIKSU-1086 */}
      <div className={myClasses.previewPhone}>{PhoneDisplay}</div>
      {isAdmin && chatUsed && (
        <span className={classes.chatUsed}>
          {' '}
          {t('application.chat.landlordUsed')}
        </span>
      )}
      <Dialog
        buttonVariant='outlined'
        dialogId={`confirm-tenant-${applicationId}`}
        noOpenDialogButton
        Content={
          <ConfirmModel
            classes={classes}
            t={t}
            dialogId={`confirm-tenant-${applicationId}`}
            loading={isLoading}
            confirmDialogAction={() => {
              setIsLoading(true)
              updateStatusWithForm({
                contractWantedId: applicationId,
                status: 'rentContractWanted',
                isFromApplicationCard: true
              })
            }}
            closeDialogAction={() =>
              closeDialog(`confirm-tenant-${applicationId}`)
            }
          />
        }
        customCloseIcon={customCloseIcon}
        borderRadius='10px'
        maxWidth='md'
      />
    </>
  )
}

export default withStyles(styles)(ApplicationCardButtons)
